import { trackWhatsNewOpened } from '@src/analytics/EventTracker';
import { useSidebarState } from '@src/hooks/useSidebarState';
import { useWhatsNew } from '@src/stores-v2/StoreContext';
import MegaphoneIcon from '@src/svgs/MegaphoneIcon';
import { AnimatePresence, motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { LABEL_VARIANTS, SIDEBAR_SPRING } from '../sidebar/sidebarMotion';
import { SidebarTooltip } from '../SidebarTooltip';
import WhatsNewDrawer from './WhatsNewDrawer';

interface WhatsNewButtonProps {
  collapsed: boolean;
}

const WhatsNewButton: React.FC<WhatsNewButtonProps> = observer(
  ({ collapsed }) => {
    const whatsNew = useWhatsNew();
    const { showLabels } = useSidebarState();

    useEffect(() => {
      whatsNew.fetchLatest();
    }, []);

    return (
      <>
        <ButtonWrapper
          $collapsed={collapsed}
          onClick={() => {
            trackWhatsNewOpened({ had_new_release: whatsNew.hasNewRelease });
            whatsNew.openDrawer();
          }}
          data-testid="whats-new-button"
        >
          <IconWrapper>
            <MegaphoneIcon />
            {whatsNew.hasNewRelease && (
              <NotificationDot data-testid="whats-new-dot" />
            )}
          </IconWrapper>
          <AnimatePresence initial={false}>
            {showLabels && (
              <motion.span
                key="whats-new-label"
                variants={LABEL_VARIANTS}
                initial="hidden"
                animate="shown"
                exit="hidden"
                transition={SIDEBAR_SPRING}
                style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
              >
                What&apos;s New
              </motion.span>
            )}
          </AnimatePresence>
          {!showLabels && <Tooltip>What&apos;s New</Tooltip>}
        </ButtonWrapper>
        <WhatsNewDrawer />
      </>
    );
  },
);

export default WhatsNewButton;

const Tooltip = styled(SidebarTooltip)``;

const ButtonWrapper = styled.button<{ $collapsed: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  width: calc(100% - 20px);
  height: 30px;
  box-sizing: border-box;
  margin: 0 10px 12px;
  /* Constant padding so the icon X stays at sidebar-x=30 in both modes,
   * with no horizontal jump during the width animation. */
  padding: 0 10px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  text-align: left;
  transition:
    background var(--transition-fast) ease,
    color var(--transition-fast) ease;

  &:hover {
    background: var(--sidebar-hover);
    color: var(--text-secondary);
  }

  &:hover ${Tooltip} {
    opacity: 1;
  }
`;

const IconWrapper = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const NotificationDot = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary);
  border: 1.5px solid var(--sidebar);
  box-shadow: var(--glow-primary);
`;
